/**
 * Back-compat adapters at the load + submit boundaries between the single
 * {@link ComplexLoginFormData} wire payload and the multi-set
 * {@link ComplexLoginMultiFormData} editing model.
 *
 * Two pure functions plus the single canonical privilege marker. The marker is
 * defined ONCE here and used by BOTH directions so the round-trip is lossless.
 *
 * Back-compat invariant: a single `standard` identity with no MFA round-trips,
 * via {@link identityToSingle}, to a {@link ComplexLoginFormData} whose four
 * fields (label, loginUrl, inputs, hint) are byte-identical to what is sent
 * today, so the COMPLEX_LOGIN case of `buildBrokerPayload` (which reads ONLY
 * those four fields and is agnostic to where `loginUrl` came from) emits
 * identical wire JSON.
 */

import { TOTP_SECRET_INPUT_KEY } from '@/sections/asset/components/tabs/credentials/totpSecret';

import {
  type ComplexLoginFormData,
  type ComplexLoginMultiFormData,
  type CredentialIdentity,
} from './types';

// Privilege is PERSISTED (LOCKED 2026-06-16) by encoding into the serialized
// credential label via this single canonical marker. `standard` adds NO suffix
// (byte-identical to today); `elevated` appends the suffix.
export const ELEVATED_LABEL_SUFFIX = ' (elevated)';

/**
 * LOAD boundary: an existing saved single Complex-Login credential → one
 * identity. Splits the TOTP magic key OUT of inputs into the typed
 * {@link CredentialIdentity.totpSecret} field (inverse of submit). The single's
 * `loginUrl` lands on the IDENTITY; the single's `hint` lands on the SHARED
 * container field. Parses the privilege marker off the label: a label ending in
 * {@link ELEVATED_LABEL_SUFFIX} restores `privilege: 'elevated'` and the clean
 * display label; otherwise `privilege: 'standard'` with the label unchanged.
 */
export function singleToMulti(
  single: ComplexLoginFormData
): ComplexLoginMultiFormData {
  const totpSecret =
    single.inputs.find(i => i.key.trim() === TOTP_SECRET_INPUT_KEY)?.value ??
    '';
  const inputs = single.inputs
    .filter(i => i.key.trim() !== TOTP_SECRET_INPUT_KEY)
    .map(i => ({ id: crypto.randomUUID(), key: i.key, value: i.value }));

  const elevated = single.label.endsWith(ELEVATED_LABEL_SUFFIX);
  const label = elevated
    ? single.label.slice(0, -ELEVATED_LABEL_SUFFIX.length)
    : single.label;

  return {
    hint: single.hint, // SHARED container field
    identities: [
      {
        id: crypto.randomUUID(),
        label,
        loginUrl: single.loginUrl, // PER-IDENTITY now (was container)
        privilege: elevated ? 'elevated' : 'standard',
        inputs: inputs.length
          ? inputs
          : [
              { id: crypto.randomUUID(), key: 'username', value: '' },
              { id: crypto.randomUUID(), key: 'password', value: '' },
            ],
        totpSecret,
      },
    ],
  };
}

/**
 * SUBMIT boundary: ONE identity → ONE existing {@link ComplexLoginFormData}
 * payload. Re-injects the typed `totpSecret` into inputs under
 * {@link TOTP_SECRET_INPUT_KEY} ONLY when non-empty, so the UNCHANGED
 * `buildBrokerPayload(COMPLEX_LOGIN)` produces today's exact wire shape.
 * `loginUrl` is sourced PER-IDENTITY; `hint` from the SHARED container. The
 * privilege marker is appended to the serialized `label` for `elevated`
 * identities only — `standard` gets NO suffix (byte-identical to today).
 */
export function identityToSingle(
  multi: ComplexLoginMultiFormData,
  identity: CredentialIdentity
): ComplexLoginFormData {
  // Dedicated totpSecret field is the single source of truth: strip any
  // user-entered totp_secret KV row before (re-)appending the canonical one,
  // mirroring the load-side filter in singleToMulti. Prevents a duplicate
  // totp_secret key in the inputs array.
  const baseInputs = identity.inputs
    .filter(i => i.key.trim() !== TOTP_SECRET_INPUT_KEY)
    .map(({ key, value }) => ({ key, value })); // strip client-only id
  const inputs = identity.totpSecret
    ? [
        ...baseInputs,
        { key: TOTP_SECRET_INPUT_KEY, value: identity.totpSecret },
      ]
    : baseInputs;

  const label =
    identity.privilege === 'elevated'
      ? identity.label + ELEVATED_LABEL_SUFFIX
      : identity.label;

  return {
    label,
    loginUrl: identity.loginUrl, // PER-IDENTITY source (was multi.loginUrl)
    inputs,
    hint: multi.hint, // SHARED container source
  };
}
